import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { structureApi, type PositionNode } from "../api/structure";
import type { ApiError } from "../api/client";
import { findNode, pruneNode, setChildren } from "../lib/positionTree";
import { idsInScope, useStructureScopeIds } from "../lib/structureScope";
import { TreeView } from "./TreeView";
import { PositionKindIcon } from "./PositionKindIcon";

/**
 * Lazy position tree used as a target picker inside the move / copy modals.
 * Loads `GET /structure/tree?depth=2` on mount, one level more per expand.
 * `excludeId` removes that node together with its subtree (a branch cannot be
 * moved or copied into itself — 409 CYCLE server-side).
 *
 * `scoped`: the target is a change of the structure, so with a structure scope (sprint 7 §12)
 * only positions inside it can be picked; the rest stay visible to find the way down, dimmed
 * with the reason.
 */
export function PositionTreePicker({
  value, onChange, excludeId, disabledIds, scoped = false,
}: {
  value: string | null;
  onChange: (node: PositionNode) => void;
  excludeId?: string;
  /** Selectable but flagged (e.g. the current parent); the caller decides what to do. */
  disabledIds?: ReadonlySet<string>;
  /** Only positions inside the user's structure scope can be picked. */
  scoped?: boolean;
}) {
  const { t } = useTranslation();
  const [roots, setRoots] = useState<PositionNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootsRef = useRef<PositionNode[]>([]);

  const commit = useCallback((next: PositionNode[]) => {
    rootsRef.current = next;
    setRoots(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    structureApi
      .tree({ depth: 2 })
      .then((list) => { if (!cancelled) commit(excludeId ? pruneNode(list, excludeId) : list); })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [commit, excludeId]);

  const onExpand = useCallback(async (node: PositionNode) => {
    try {
      const kids = await structureApi.tree({ parentId: node.id, depth: 1 });
      const filtered = excludeId ? pruneNode(kids, excludeId) : kids;
      commit(setChildren(rootsRef.current, node.id, filtered));
    } catch (err) {
      setError((err as ApiError).message);
    }
  }, [commit, excludeId]);

  const scope = useStructureScopeIds();
  // null = every position can be picked.
  const pickable = useMemo(
    () => (scoped && scope.size > 0 && roots ? idsInScope(scope, roots) : null),
    [scoped, scope, roots],
  );
  const outOfScopeTip = t("structure.picker.outOfScope", "Van vašeg opsega izmena strukture");
  const pick = useCallback((n: PositionNode) => {
    if (pickable && !pickable.has(n.id)) return;
    onChange(n);
  }, [pickable, onChange]);

  const getId = useCallback((n: PositionNode) => n.id, []);
  const getChildren = useCallback((n: PositionNode) => n.children, []);
  const getHasChildren = useCallback((n: PositionNode) => n.hasChildren, []);
  const getLabel = useCallback(
    (n: PositionNode) => (
      <span
        style={{ opacity: disabledIds?.has(n.id) || (pickable && !pickable.has(n.id)) ? 0.55 : 1 }}
        title={pickable && !pickable.has(n.id) ? outOfScopeTip : undefined}
      >
        {n.name}
        {n.sequenceNo !== null && <span className="muted"> #{n.sequenceNo}</span>}
        {n.code && <span className="muted" style={{ fontFamily: "var(--mono)", fontSize: 12, marginLeft: 6 }}>{n.code}</span>}
      </span>
    ),
    [disabledIds, pickable, outOfScopeTip],
  );

  if (error) return <div className="empty" role="alert">{error}</div>;
  if (roots === null) return <div className="empty">{t("common.loading", "Učitavanje…")}</div>;

  const selectedNode = value ? findNode(roots, value) : null;
  return (
    <>
    {pickable && (
      <span className="muted" style={{ fontSize: 12.5 }}>
        {t("structure.picker.scopeHint", "Možete da izaberete samo poziciju u svom opsegu izmena strukture; ostale su prigušene.")}
      </span>
    )}
    <div className="tree-picker">
      <TreeView<PositionNode>
        nodes={roots}
        getId={getId}
        getLabel={getLabel}
        getChildren={getChildren}
        getHasChildren={getHasChildren}
        onExpand={onExpand}
        selectedId={selectedNode ? value : null}
        onSelect={pick}
        renderIcon={(n) => <PositionKindIcon kind={n.kind} />}
        defaultExpandedDepth={2}
        ariaLabel={t("structure.picker.aria", "Izbor ciljne pozicije")}
        emptyText={t("structure.empty", "Struktura je prazna.")}
        expandLabel={t("tree.expand", "Proširi")}
        collapseLabel={t("tree.collapse", "Skupi")}
        loadingLabel={t("common.loading", "Učitavanje…")}
      />
    </div>
    </>
  );
}
